"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { createThread, type ForumCategory } from "@/app/actions/forum";
import type {
  ForumThreadContext,
  ForumTopicTag,
} from "@/lib/forum/thread-context";
import type { PublicEventSportKey } from "@/lib/public-events/types";

type RetainedContext = {
  categorySlug: string | null;
  sportKey: PublicEventSportKey | null;
  conditionLocationSlug: string | null;
  conditionLocationLabel: string | null;
  regionSlug: string | null;
  seasonKey: string | null;
  topicTags: ForumTopicTag[];
};

function labelFromKey(value: string): string {
  return value
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function ContextChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove ${label} context`}
      className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gold/25 bg-white px-4 py-2 text-left text-[12px] font-medium text-navy transition-colors hover:border-gold/60 hover:bg-gold/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
    >
      <span>{label}</span>
      <span aria-hidden="true" className="text-[16px] leading-none text-gold">
        ×
      </span>
    </button>
  );
}

export function NewThreadForm({
  categories,
  context,
}: {
  categories: ForumCategory[];
  context: ForumThreadContext;
}) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState("");
  const contextualCategory = categories.find(
    (category) => category.slug === context.categorySlug,
  );
  const [categoryId, setCategoryId] = useState(contextualCategory?.id ?? "");
  const [retained, setRetained] = useState<RetainedContext>(() => ({
    ...context,
    categorySlug: contextualCategory?.slug ?? null,
  }));

  const hasContext = Boolean(
    retained.categorySlug ||
      retained.sportKey ||
      retained.conditionLocationSlug ||
      retained.regionSlug ||
      retained.seasonKey ||
      retained.topicTags.length > 0,
  );

  function removeTopic(topic: ForumTopicTag) {
    setRetained((current) => ({
      ...current,
      topicTags: current.topicTags.filter((value) => value !== topic),
    }));
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const formData = new FormData(event.currentTarget);

    startTransition(async () => {
      const result = await createThread(formData);
      if (result.success && result.categorySlug && result.slug) {
        router.push(`/community/forum/${result.categorySlug}/${result.slug}`);
        return;
      }
      setError(
        result.error ??
          "The forum is temporarily unavailable. Please try again.",
      );
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 rounded-[18px] border border-border-light bg-white p-6 shadow-sm md:p-8"
    >
      <div className="rounded-r-[12px] border-y border-r border-gold/25 border-l-4 border-l-gold bg-gold/5 px-5 py-4">
        <p className="text-[13px] font-bold text-navy">
          This discussion is public
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-text-body">
          Do not include Crew or Squad details, a live location, private recommendations or private notes.
        </p>
      </div>

      {hasContext ? (
        <fieldset className="rounded-[14px] border border-border-light bg-warm-bg/60 p-4">
          <legend className="px-1 text-[11px] font-bold uppercase tracking-[0.14em] text-text-muted">
            Context from Companion
          </legend>
          <p className="mb-3 mt-1 text-[12px] leading-relaxed text-text-muted">
            Select any chip to remove it before publishing.
          </p>
          <div className="flex flex-wrap gap-2">
            {retained.categorySlug ? (
              <ContextChip
                label={`Category: ${contextualCategory?.name ?? labelFromKey(retained.categorySlug)}`}
                onRemove={() => {
                  setRetained((current) => ({
                    ...current,
                    categorySlug: null,
                  }));
                  setCategoryId("");
                }}
              />
            ) : null}
            {retained.sportKey ? (
              <ContextChip
                label={`Sport: ${labelFromKey(retained.sportKey)}`}
                onRemove={() =>
                  setRetained((current) => ({ ...current, sportKey: null }))
                }
              />
            ) : null}
            {retained.conditionLocationSlug ? (
              <ContextChip
                label={`Place: ${retained.conditionLocationLabel ?? labelFromKey(retained.conditionLocationSlug)}`}
                onRemove={() =>
                  setRetained((current) => ({
                    ...current,
                    conditionLocationSlug: null,
                    conditionLocationLabel: null,
                  }))
                }
              />
            ) : null}
            {retained.regionSlug ? (
              <ContextChip
                label={`Region: ${labelFromKey(retained.regionSlug)}`}
                onRemove={() =>
                  setRetained((current) => ({ ...current, regionSlug: null }))
                }
              />
            ) : null}
            {retained.seasonKey ? (
              <ContextChip
                label={`Season: ${labelFromKey(retained.seasonKey)}`}
                onRemove={() =>
                  setRetained((current) => ({ ...current, seasonKey: null }))
                }
              />
            ) : null}
            {retained.topicTags.map((topic) => (
              <ContextChip
                key={topic}
                label={`Topic: ${labelFromKey(topic)}`}
                onRemove={() => removeTopic(topic)}
              />
            ))}
          </div>
        </fieldset>
      ) : null}

      {retained.sportKey ? (
        <input type="hidden" name="sport_key" value={retained.sportKey} />
      ) : null}
      {retained.conditionLocationSlug ? (
        <input
          type="hidden"
          name="condition_location_slug"
          value={retained.conditionLocationSlug}
        />
      ) : null}
      {retained.regionSlug ? (
        <input type="hidden" name="region_slug" value={retained.regionSlug} />
      ) : null}
      {retained.seasonKey ? (
        <input type="hidden" name="season_key" value={retained.seasonKey} />
      ) : null}
      {retained.topicTags.map((topic) => (
        <input key={topic} type="hidden" name="topic_tags" value={topic} />
      ))}

      {error ? (
        <div
          role="alert"
          aria-live="polite"
          className="rounded-[10px] border border-error/20 bg-error/5 px-4 py-3 text-[13px] text-error"
        >
          {error}
        </div>
      ) : null}

      <div>
        <label
          htmlFor="category_id"
          className="mb-2 block text-[13px] font-medium text-navy"
        >
          Category
        </label>
        <select
          id="category_id"
          name="category_id"
          required
          value={categoryId}
          onChange={(event) => {
            const nextId = event.target.value;
            setCategoryId(nextId);
            if (nextId !== contextualCategory?.id) {
              setRetained((current) => ({
                ...current,
                categorySlug: null,
              }));
            }
          }}
          className="min-h-11 w-full appearance-none rounded-[10px] border border-border-light bg-warm-bg px-4 py-3 text-[14px] text-text-body focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="title"
          className="mb-2 block text-[13px] font-medium text-navy"
        >
          Title
        </label>
        <input
          id="title"
          name="title"
          type="text"
          required
          autoComplete="off"
          placeholder="What would you like to discuss?"
          className="min-h-11 w-full rounded-[10px] border border-border-light bg-warm-bg px-4 py-3 text-[14px] text-text-body placeholder:text-text-muted/50 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
        />
      </div>

      <div>
        <label
          htmlFor="body"
          className="mb-2 block text-[13px] font-medium text-navy"
        >
          Discussion
        </label>
        <textarea
          id="body"
          name="body"
          required
          rows={8}
          placeholder="Share a public question, tip or experience..."
          className="w-full resize-y rounded-[10px] border border-border-light bg-warm-bg px-4 py-3 text-[14px] text-text-body placeholder:text-text-muted/50 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
        />
      </div>

      <div>
        <label
          htmlFor="location_tag"
          className="mb-2 block text-[13px] font-medium text-navy"
        >
          Public location tag{" "}
          <span className="font-normal text-text-muted">(optional)</span>
        </label>
        <input
          id="location_tag"
          name="location_tag"
          type="text"
          placeholder="e.g. Chamonix, Bali, Antibes"
          className="min-h-11 w-full rounded-[10px] border border-border-light bg-warm-bg px-4 py-3 text-[14px] text-text-body placeholder:text-text-muted/50 focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
        />
      </div>

      <button
        type="submit"
        disabled={isPending}
        className="inline-flex min-h-11 w-full items-center justify-center rounded-[10px] bg-navy px-5 py-3 text-[14px] font-medium text-white transition-colors hover:bg-navy-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isPending ? "Creating discussion..." : "Create discussion"}
      </button>
    </form>
  );
}
